import { Icon, Tooltip } from "@blueprintjs/core"
import React from "react"
import { useFormContext } from "react-hook-form"

import styles from "../ProjectBudgetForm.module.css"

import { getDefaultRate } from "~/helpers/rate-helpers"

import DropdownSelector from "~/common/DropdownSelector"

import { RateCardOption } from "~/forms/ProjectBudgetForm/ProjectBudgetForm"
import ProjectRateItemRow from "~/forms/ProjectBudgetForm/ProjectRates/ProjectRateItemRow"

import { useProjectBudgetFormContext } from "../ProjectBudgetFormProvider"
import RoleAdder from "../RoleAdder"
import Column from "../TableColumn"
import Row from "../TableRow"
import { ProjectBudgetFormData } from "../formTypes"
import { BudgetedRole, RateCard } from "../types"

type Props = {
  rateCard: RateCard
  defaultFullTimeMinutes: number
  rateCards: ReadonlyArray<RateCardOption>
  unallocatedRoles: Array<BudgetedRole>
  onAddRole: (id: number) => void
  onShowAllRoles: () => void
}

const ProjectRatesEditor = (props: Props) => {
  const {
    rateCard,
    defaultFullTimeMinutes,
    rateCards,
    unallocatedRoles,
    onAddRole,
    onShowAllRoles,
  } = props

  const { watch, setValue } = useFormContext<ProjectBudgetFormData>()
  const formData = watch()

  const { showFinancialColumns, hideFinancials, categorizedRoles } =
    useProjectBudgetFormContext()

  const selectedRateCard = {
    value: formData.rateCardId,
    label:
      rateCards.find((rc) => rc.value === formData.rateCardId)?.label || "",
  }

  if (hideFinancials) {
    return null
  }

  // Fetch the budgeted rate and rate card rate for a given role
  const ratesFromRole = (role: BudgetedRole) => {
    const projectRate = formData.projectRates.find(
      (rate) => rate.roleId === role.id,
    )
    const rateCardRate = getDefaultRate(rateCard, role.id)

    return {
      projectRateObject: projectRate,
      projectRate: projectRate?.rate,
      rateCardRate,
    }
  }

  const handleChangeRateCard = (newRateCard: RateCardOption) => {
    setValue("rateCardId", newRateCard.value)
  }

  // Transfer all project rate card rates to project rates
  const transferAllRateCardRates = () => {
    const newRates = [...formData.projectRates]

    categorizedRoles.people.forEach((role) => {
      const { projectRateObject, rateCardRate } = ratesFromRole(role)
      const updatedRate = {
        ...projectRateObject,
        rate: rateCardRate,
      }

      const index = newRates.findIndex((r) => r.roleId === updatedRate.roleId)
      if (index === -1) {
        newRates.push(updatedRate)
      } else {
        newRates[index] = updatedRate
      }
    })

    setValue("projectRates", newRates)
  }

  const transferProjectRatesEnabled = categorizedRoles.people.some((role) => {
    const { projectRate, rateCardRate } = ratesFromRole(role)
    return rateCardRate !== projectRate
  })

  return (
    <div className={styles.projectRatesWrapper}>
      <Row heading>
        <Column>Role</Column>
        {showFinancialColumns && (
          <>
            <Column className={styles.alignRight}>
              <DropdownSelector
                selectedValue={selectedRateCard.value}
                options={rateCards}
                onChange={handleChangeRateCard}
                dataTestId="project-rate-card"
                className={styles.rateCardDropdown}
              />
            </Column>
            <Column>
              {transferProjectRatesEnabled && (
                <Tooltip content={"Copy all rates to project"} placement="top">
                  <Icon
                    icon="arrow-right"
                    className={styles.transferRateArrow}
                    data-test="transfer-all-rates-icon"
                    onClick={transferAllRateCardRates}
                  />
                </Tooltip>
              )}
            </Column>
            <Column className={styles.alignRight}>Project Rate</Column>
          </>
        )}
      </Row>
      <div className={styles.tableBody}>
        {categorizedRoles.people.map((role) => {
          const rateCardRate = getDefaultRate(rateCard, role.id)

          return (
            <ProjectRateItemRow
              key={role.id}
              budgetedRole={role}
              rateCardRate={rateCardRate}
              defaultFullTimeMinutes={defaultFullTimeMinutes}
            />
          )
        })}
      </div>
      {unallocatedRoles.length > 0 && (
        <div className={styles.tableWidget}>
          <RoleAdder
            placeholder="Add Roles"
            roles={unallocatedRoles}
            onAddRole={onAddRole}
            onShowAllRoles={onShowAllRoles}
            excludeOtherExpenses
          />
        </div>
      )}
    </div>
  )
}

export default ProjectRatesEditor
